import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { parseHeader, drawReportHeader } from "../../../../util/headerUtil";

export const printAdmissionA4 = (formData, authCtx) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  printAdmissionLogic(doc, formData, authCtx);
};

// Fallback for any legacy calls
export const printAdmissionA5 = (formData, authCtx) => {
  printAdmissionA4(formData, authCtx);
};

const printAdmissionLogic = (doc, formData, authCtx) => {
  const hospitalData = authCtx?.hospitalData;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  // Use Standardized Report Header
  const startY = drawReportHeader(doc, hospitalData, "ADMISSION CARD", {
    hideSeparator: true,
    isExport: true,
    margin: 0,
    top: 0
  });

  let currentY = startY + 5;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 10;

  // ----------------------------------------------------------------------
  // Helper to draw Section Box
  // ----------------------------------------------------------------------
  const drawSection = (title, contentFunc, startY) => {
    const boxW = pageWidth - margin * 2;
    const headerH = 8;

    doc.setFillColor(20, 109, 136); // Matching theme
    doc.rect(margin, startY, boxW, headerH, 'F');

    doc.setFont("helvetica", "bold").setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), margin + 3, startY + 5.5);

    let contentY = startY + headerH;
    doc.setTextColor(0, 0, 0);
    let contentH = contentFunc(contentY, boxW);

    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, startY + headerH, boxW, contentH);

    return startY + headerH + contentH + 6;
  };

  const p = formData.patientId || {};
  const data = {
    ipdNumber: formData.ipdAdmissionId || "-",
    date: formData.admissionDateTime ? formData.admissionDateTime.split("T")[0] : "-",
    patientName: [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || "-",
    address: p.address || "-",
    mobile: p.mobile || "-",
    age: p.age || "-",
    gender: p.gender || "-",
    referredBy: formData.referredBy || p.referredBy || "-",
    admissionReason: formData.admissionReason || "-",
    bloodGroup: formData.bloodGroup || "-",
    floor: formData.wardBed ? formData.wardBed.floor : "-",
    wardName: formData.wardBed ? formData.wardBed.wardName : "-",
    bedNumber: formData.wardBed ? formData.wardBed.bedNumber : "-",
    guardianName: formData.guardianName || "-",
    guardianRelation: formData.guardianRelation || "-",
    guardianContact: formData.guardianContactNumber || "-",
    tpaId: formData.tpaId || "-",
    tpaValidity: formData.tpaValidity ? formData.tpaValidity.split("T")[0] : "-",
    adhar: formData.adharCardNo || "-",
    pan: formData.panCardNo || "-",
    insuranceCard: formData.insuranceCardNo || "-",
    symptomsTitle: formData.symptomsTitle || "-",
    symptomsType: formData.option2 || "-",
    symptomsDesc: formData.symptomsDesc || "-",
    diagnosis: formData.diagnosis || "-",
    bp: formData.bp || (formData.bp_systolic || formData.bp_diastolic ? `${formData.bp_systolic || "0"}/${formData.bp_diastolic || "0"}` : "-"),
    pulse: formData.pulse || "-",
    spo2: formData.spo2 || "-",
    temp: formData.temperature || "-",
    weight: formData.weight || "-",
    height: formData.height || "-",
    pho: formData.pho || "-",
    co: formData.co || "-",
    kco: formData.kco || "-",
    cf: formData.cf || "-"
  };

  const bodyFontSize = 10;
  const rowHeight = 7;

  // 1. ADMISSION DETAILS
  currentY = drawSection("Admission Details", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const col2 = margin + (w / 2) + 2;
    const labelW = 35;

    const drawRow = (l1, v1, l2, v2) => {
      doc.setFont("helvetica", "bold");
      doc.text(l1, margin + 3, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(v1), margin + labelW, y);

      if (l2) {
        doc.setFont("helvetica", "bold");
        doc.text(l2, col2, y);
        doc.setFont("helvetica", "normal");
        doc.text(String(v2), col2 + labelW, y);
      }
      y += rowHeight;
    };

    drawRow("Patient Name:", data.patientName, "Adm Date:", data.date);
    drawRow("Age / Gender:", `${data.age} / ${data.gender}`, "Blood Group:", data.bloodGroup);
    drawRow("Contact:", data.mobile, "IPD No:", data.ipdNumber);

    const splitAddr = doc.splitTextToSize(data.address, (w / 2) - labelW - 2);
    doc.setFont("helvetica", "bold");
    doc.text("Address:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(splitAddr, margin + labelW, y);

    const combinedWardInfo = `${data.floor} / ${data.wardName} - ${data.bedNumber}`;
    doc.setFont("helvetica", "bold");
    doc.text("Ward Details:", col2, y);
    doc.setFont("helvetica", "normal");
    doc.text(combinedWardInfo, col2 + labelW, y);

    y = Math.max(y + rowHeight, y + (splitAddr.length * 5));

    drawRow("Referred By:", data.referredBy, "Adm Reason:", data.admissionReason);

    return y - cY + 2;
  }, currentY);

  // 2. DIAGNOSIS & SYMPTOMS
  currentY = drawSection("Diagnosis & Symptoms", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const labelW = 35;

    doc.setFont("helvetica", "bold");
    doc.text("Symptoms:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${data.symptomsTitle} (${data.symptomsType})`, margin + labelW, y);
    y += rowHeight;

    const splitDiag = doc.splitTextToSize(data.diagnosis || "-", w - labelW - 5);
    doc.setFont("helvetica", "bold");
    doc.text("Diagnosis:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(splitDiag, margin + labelW, y);
    y += (splitDiag.length * 5) + 2;

    const splitDesc = doc.splitTextToSize(data.symptomsDesc || "-", w - labelW - 5);
    doc.setFont("helvetica", "bold");
    doc.text("Symp Desc:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(splitDesc, margin + labelW, y);
    y += (splitDesc.length * 5) + 2;

    return y - cY;
  }, currentY);

  // 3. GUARDIAN INFORMATION
  currentY = drawSection("Guardian Information", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const col2 = margin + (w / 2) + 2;
    const labelW = 35;

    const guardianText = `${data.guardianName} (${data.guardianRelation})`;
    doc.setFont("helvetica", "bold");
    doc.text("Guardian:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(guardianText, margin + labelW, y);

    doc.setFont("helvetica", "bold");
    doc.text("Contact:", col2, y);
    doc.setFont("helvetica", "normal");
    doc.text(data.guardianContact, col2 + labelW, y);
    y += rowHeight;

    return y - cY;
  }, currentY);

  // 4. INSURANCE & PAYMENT DETAILS
  currentY = drawSection("Insurance & Payment Details", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const col2 = margin + (w / 2) + 2;
    const labelW = 35;

    doc.setFont("helvetica", "bold");
    doc.text("Adhar/PAN:", margin + 3, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${data.adhar} / ${data.pan}`, margin + labelW, y);

    doc.setFont("helvetica", "bold");
    doc.text("Insurance:", col2, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${data.insuranceCard}`, col2 + labelW, y);
    y += rowHeight;

    return y - cY;
  }, currentY);

  // 5. VITAL SIGNS
  currentY = drawSection("Vital Signs", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const colW = w / 6;
    doc.text(`BP: ${data.bp}`, margin + 2, y);
    doc.text(`Pls: ${data.pulse}`, margin + colW, y);
    doc.text(`SpO2: ${data.spo2}`, margin + colW * 2, y);
    doc.text(`Temp: ${data.temp}`, margin + colW * 3, y);
    doc.text(`Wt: ${data.weight}`, margin + colW * 4, y);
    doc.text(`Ht: ${data.height}`, margin + colW * 5, y);
    return 10;
  }, currentY);

  // 6. MEDICAL HISTORY & FINDINGS
  currentY = drawSection("Medical History & Findings", (cY, w) => {
    doc.setFont("helvetica", "normal").setFontSize(bodyFontSize);
    let y = cY + 5;
    const labelW = 35;

    const drawLongRow = (label, value) => {
      const splitVal = doc.splitTextToSize(String(value), w - labelW - 5);
      doc.setFont("helvetica", "bold");
      doc.text(label, margin + 3, y);
      doc.setFont("helvetica", "normal");
      doc.text(splitVal, margin + labelW, y);
      y += (splitVal.length * 5) + 2;
    };

    drawLongRow("PHO:", data.pho);
    drawLongRow("CO:", data.co);
    drawLongRow("KCO:", data.kco);
    drawLongRow("CF:", data.cf);

    return y - cY;
  }, currentY);

  // FOOTER
  const consentPart1 =
    "I, the undersigned, hereby voluntarily admit myself/my relative for medical treatment on the advice of the attending doctor. " +
    "I have been informed in detail about my illness, the required investigations, and the treatment advised. " +
    "I fully understand that there may be risks and complications associated with the treatment and that I accept full responsibility for the same.";

  const consentPart2 =
    "I authorize the doctors and staff to carry out the necessary investigations and treatment. " +
    "I also understand that the cost of treatment, medicines, and investigations will be my responsibility " +
    "and that the charges may vary as per requirements. I willingly provide my consent for the treatment.";

  const fullConsent = `${consentPart1} ${consentPart2}`;
  const splitConsent = doc.splitTextToSize(fullConsent, pageWidth - margin * 2);
  const consentHeight = (splitConsent.length * 4) + 15;

  if (currentY + consentHeight > pageHeight - 30) {
    doc.addPage();
    currentY = 20;
  }

  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("CONSENT / UNDERTAKING", margin, currentY + 3);

  doc.setFont("helvetica", "normal").setFontSize(8.5);
  doc.setTextColor(60, 60, 60);
  doc.text(splitConsent, margin, currentY + 8);

  const signY = pageHeight - 25;
  if (currentY + consentHeight > signY - 10) {
      // If consent is too close to sig line, we might add a page or just move signY
      // But usually A4 has enough space.
  }
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setDrawColor(0, 0, 0);
  doc.line(margin, signY, margin + 50, signY);
  doc.text("Patient / Guardian Signature", margin + 25, signY + 4, { align: "center" });

  doc.line(pageWidth - margin - 50, signY, pageWidth - margin, signY);
  doc.text("Authority Sign / Doctor Sign", pageWidth - margin - 25, signY + 4, { align: "center" });

  const userRole = authCtx?.role;
  const userName = authCtx?.userName;
  if (userRole === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(8);
    doc.text(`( ${userName} )`, pageWidth - margin - 25, signY + 10, { align: "center" });
  }

  doc.save(`${data.patientName}-AdmissionCard.pdf`);
};
